import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DiscordRolePicker } from '@/components/discord/DiscordPickers';
import { Switch } from '@/components/ui/switch';
import { TriggerEditor, type TriggerKind } from '@/components/roles/TriggerEditor';
import { ApiError } from '@/lib/api';
import { useRoleRules, useUpdateRoleRule, useRegenerateRoleRuleSecret } from '@/lib/queries';
import { useCan } from '@/lib/use-can';

export function RoleRuleDetailPage() {
  const { id = '' } = useParams();
  const list = useRoleRules();
  const update = useUpdateRoleRule();
  const regen = useRegenerateRoleRuleSecret();
  const can = useCan();

  const rule = list.data?.find((r) => r.id === id);

  const [name, setName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [triggerKind, setTriggerKind] = useState<TriggerKind>('on_join');
  const [triggerConfig, setTriggerConfig] = useState<Record<string, unknown>>({});
  const [newSecret, setNewSecret] = useState<string | null>(null);

  useEffect(() => {
    if (!rule) return;
    setName(rule.name);
    setRoleId(rule.roleId);
    setEnabled(rule.enabled);
    setTriggerKind(rule.triggerKind);
    setTriggerConfig(rule.triggerConfig);
  }, [rule]);

  if (!can('roles.manage')) {
    return <p className="text-muted-foreground">You do not have permission to manage roles.</p>;
  }

  if (list.isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (!rule) {
    return (
      <div>
        <p className="text-muted-foreground">Rule not found.</p>
        <Button asChild variant="ghost" size="sm" className="mt-2">
          <Link to="/roles">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to list
          </Link>
        </Button>
      </div>
    );
  }

  async function handleSave() {
    try {
      await update.mutateAsync({
        id,
        patch: { name, roleId, enabled, triggerKind, triggerConfig },
      });
      toast.success('Saved');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/roles">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to list
            </Link>
          </Button>
          <h1 className="mt-2 flex items-center gap-2 text-3xl font-bold tracking-tight">
            {name}
            <Badge variant={enabled ? 'default' : 'secondary'}>{enabled ? 'on' : 'off'}</Badge>
          </h1>
        </div>
        <Button onClick={handleSave} disabled={update.isPending}>
          <Save className="mr-2 h-4 w-4" />
          Save
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Basics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Role to grant</Label>
            <DiscordRolePicker value={roleId} onValueChange={setRoleId} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={setEnabled} id="enabled" />
            <Label htmlFor="enabled">Enabled</Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Trigger</CardTitle>
        </CardHeader>
        <CardContent>
          <TriggerEditor
            kind={triggerKind}
            config={triggerConfig}
            onKindChange={setTriggerKind}
            onConfigChange={setTriggerConfig}
          />
        </CardContent>
      </Card>

      {rule.triggerKind === 'webhook_event' && (
        <Card>
          <CardHeader>
            <CardTitle>Webhook</CardTitle>
            <CardDescription>
              External services POST to this URL with HMAC-SHA256 signature in
              <code className="ml-1 rounded bg-muted px-1">X-Raynex-Signature</code> to grant this
              role.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <Label>URL</Label>
              <Input
                readOnly
                value={`${window.location.origin}/api/webhooks/roles/${rule.id}`}
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
            <div className="space-y-1">
              <Label>Secret last rotated</Label>
              <p className="text-sm text-muted-foreground">
                {rule.webhookSecretLastRotatedAt
                  ? new Date(rule.webhookSecretLastRotatedAt).toLocaleString()
                  : 'never'}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!confirm('Regenerate secret? Old secret stops working immediately.')) return;
                const res = await regen.mutateAsync(id);
                setNewSecret(res.webhookSecret);
              }}
            >
              Regenerate secret
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={Boolean(newSecret)} onOpenChange={(o) => !o && setNewSecret(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New secret</DialogTitle>
            <DialogDescription>Copy now — won't be shown again.</DialogDescription>
          </DialogHeader>
          <Input
            readOnly
            value={newSecret ?? ''}
            onFocus={(e) => e.currentTarget.select()}
            className="my-3"
          />
          <DialogFooter>
            <Button onClick={() => setNewSecret(null)}>I saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
